import {
    forwardRef,
    useState,
    useRef,
    type FC,
    memo
} from "react";
import {
    Pressable,
    Animated,
    Platform,
    View
} from "react-native";
import type IColorSwatchPickerProps from "./type";
import type {
    ColorSwatchPickerKeyboardEventType,
    ColorSwatchPickerAddProps
} from "./type";
import stylesheet, {
    getColorSwatchPickerAddStyle,
    useStyles
} from "./stylesheet";
import {
    COLOR_SWATCH_PICKER_NAVIGATION_KEYS,
    COLOR_SWATCH_PICKER_DEFAULT_COLUMNS,
    getColorSwatchPickerNextIndex,
    getColorSwatchPickerTabStop,
    getColorSwatchPickerIsMatch
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    usePressScale
} from "../../helpers/motion";
import {
    usePointerType
} from "../../helpers/pointer";
import type {
    RefForwardingComponent
} from "../../types";
import {
    Plus as PlusIcon
} from "lucide-react-native";
import {
    getColorSwatchSize
} from "../colorSwatch/stylesheet";
import {
    getHitArea
} from "../../helpers/theme/control";
import ColorSwatch from "../colorSwatch";
import StateLayer from "../stateLayer";

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

const ColorSwatchPickerAdd: RefForwardingComponent<View, ColorSwatchPickerAddProps> = ({
    customLocalize,
    customTheme,
    isDisabled,
    swatchSize,
    onKeyDown,
    tabIndex,
    onFocus,
    onPress,
    ...props
}, ref) => {
    const {
        touchTargets,
        iconSizes,
        radiuses,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled,
        onFocus
    });

    const pressScaleStyle = usePressScale({
        customTheme,
        isDisabled,
        isPressed
    });

    const hitArea = getHitArea({
        visualSize: swatchSize,
        touchTargets,
        pointerType
    });

    const addDynamicStyle = getColorSwatchPickerAddStyle({
        isFocusVisible,
        isDisabled,
        swatchSize,
        radiuses,
        borders,
        colors
    });

    const webProps = Platform.OS === "web" ? {
        onKeyDown,
        tabIndex
    } : {};

    return <AnimatedPressable
        {...props}
        {...webProps}
        {...interactionHandlers}
        hitSlop={Math.max(0, (hitArea.minHeight - swatchSize) / 2)}
        accessibilityLabel={localize("colorSwatchPicker-add")}
        aria-label={localize("colorSwatchPicker-add")}
        aria-disabled={isDisabled}
        aria-haspopup="dialog"
        disabled={isDisabled}
        style={[
            stylesheet.add,
            addDynamicStyle,
            pressScaleStyle
        ]}
        onPress={onPress}
        role="button"
        ref={ref}
    >
        <StateLayer
            borderRadius={radiuses.full}
            customTheme={customTheme}
            isHovered={isHovered}
            isPressed={isPressed}
        />
        <PlusIcon
            color={isDisabled ? colors.system.state.content.disabled.neutral : colors.content.icon.mid}
            size={iconSizes.small}
        />
    </AnimatedPressable>;
};

const ColorSwatchPickerAddSlot = forwardRef(ColorSwatchPickerAdd);

const ColorSwatchPicker: FC<IColorSwatchPickerProps> = ({
    columns = COLOR_SWATCH_PICKER_DEFAULT_COLUMNS,
    "aria-label": ariaLabelProp,
    isAddEnabled = false,
    isDisabled = false,
    value: valueProp,
    size = "medium",
    customLocalize,
    layout = "row",
    initialValue,
    surfaceColor,
    customTheme,
    onAddPress,
    onChange,
    items,
    style,
    title,
    ...props
}) => {
    const {
        controlHeights,
        iconSizes,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const entryRefs = useRef<Array<View | null>>([]);

    const [
        innerValue,
        setInnerValue
    ] = useState(initialValue);

    const [
        lastFocused,
        setLastFocused
    ] = useState<number | null>(null);

    const value = valueProp ?? innerValue;

    const count = items.length + (isAddEnabled ? 1 : 0);

    const selectedIndex = items.findIndex((item) => getColorSwatchPickerIsMatch({
        color: item.color,
        value
    }));

    const tabStop = getColorSwatchPickerTabStop({
        selectedIndex,
        lastFocused,
        isDisabled,
        count
    });

    const currentSize = getColorSwatchSize({
        controlHeights,
        iconSizes,
        spaces,
        size
    });

    const {
        container: containerDynamicStyle
    } = useStyles({
        swatchSize: currentSize.size,
        columns,
        layout,
        spaces
    });

    const handleSelect = (index: number) => () => {
        const item = items[index];

        if(!item) {
            return;
        }

        setInnerValue(item.color);

        onChange?.(item.color, item);
    };

    const handleKeyDown = (index: number) => (event: ColorSwatchPickerKeyboardEventType) => {
        if(isDisabled || event.target !== event.currentTarget || event.altKey || event.ctrlKey || event.metaKey) {
            return;
        }

        if(event.key === " " || event.key === "Enter") {
            event.preventDefault();

            if(index >= items.length) {
                onAddPress?.();

                return;
            }

            handleSelect(index)();

            return;
        }

        if(!COLOR_SWATCH_PICKER_NAVIGATION_KEYS.includes(event.key)) {
            return;
        }

        event.preventDefault();

        const next = getColorSwatchPickerNextIndex({
            key: event.key,
            direction,
            columns,
            layout,
            count,
            index
        });

        if(next === null) {
            return;
        }

        entryRefs.current[next]?.focus();

        handleSelect(next)();
    };

    const getWebProps = (index: number) => Platform.OS === "web" ? {
        tabIndex: tabStop === index ? 0 as const : -1 as const,
        onKeyDown: handleKeyDown(index)
    } : {};

    return <View
        {...props}
        accessibilityLabel={ariaLabelProp ?? title ?? localize("colorSwatchPicker-label")}
        aria-label={ariaLabelProp ?? title ?? localize("colorSwatchPicker-label")}
        aria-disabled={isDisabled}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        role="radiogroup"
    >
        {
            items.map((item, index) => <ColorSwatch
                {...getWebProps(index)}
                aria-checked={selectedIndex === index}
                onFocus={() => setLastFocused(index)}
                ref={(node) => {
                    entryRefs.current[index] = node;
                }}
                isSelected={selectedIndex === index}
                customLocalize={customLocalize}
                key={`${item.color}-${index}`}
                onPress={handleSelect(index)}
                surfaceColor={surfaceColor}
                customTheme={customTheme}
                isDisabled={isDisabled}
                title={item.title}
                color={item.color}
                role="radio"
                size={size}
            />)
        }
        {
            isAddEnabled && <ColorSwatchPickerAddSlot
                {...getWebProps(items.length)}
                onFocus={() => setLastFocused(items.length)}
                ref={(node) => {
                    entryRefs.current[items.length] = node;
                }}
                customLocalize={customLocalize}
                swatchSize={currentSize.size}
                customTheme={customTheme}
                isDisabled={isDisabled}
                onPress={onAddPress}
            />
        }
    </View>;
};
export default memo(ColorSwatchPicker);
